'use client';
import React, { useState } from 'react';


import type { SignUpFormData } from './SellerSignUpForm';
import Icon from '@/components/ui/AppIcon';

interface Props {
  form: ReturnType<typeof import('react-hook-form').useForm<SignUpFormData>>;
}

export default function Step1Account({ form }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    register,
    formState: { errors },
    watch,
  } = form;

  const password = watch('password');

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-700 text-foreground mb-1">
          Account Details
        </h2>
        <p className="text-sm text-muted-foreground">
          These credentials will be used to log into your seller account.
        </p>
      </div>

      {/* Full Name */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Full Name <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          As it appears on your PAN card
        </p>
        <input
          type="text"
          placeholder="Rahul Sharma"
          className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
          {...register('fullName', {
            required: 'Full name is required',
            minLength: { value: 2, message: 'Name must be at least 2 characters' },
          })}
        />
        {errors.fullName && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.fullName.message}
          </p>
        )}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Email Address <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Order notifications and account updates will be sent here
        </p>
        <input
          type="email"
          placeholder="rahul@yourstore.in"
          className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email address',
            },
          })}
        />
        {errors.email && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Mobile Number <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Indian mobile number — used for WhatsApp support and order updates
        </p>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 border border-input rounded-xl px-3 py-2.5 bg-muted/50 text-sm text-muted-foreground flex-shrink-0">
            🇮🇳 +91
          </div>
          <input
            type="tel"
            placeholder="98765 43210"
            maxLength={10}
            className="flex-1 border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            {...register('phone', {
              required: 'Mobile number is required',
              pattern: {
                value: /^[6-9]\d{9}$/,
                message: 'Enter a valid 10-digit Indian mobile number',
              },
            })}
          />
        </div>
        {errors.phone && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.phone.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Password <span className="text-error">*</span>
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Minimum 8 characters with at least one number
        </p>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            className="w-full border border-input rounded-xl px-4 py-2.5 pr-11 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
              pattern: {
                value: /(?=.*\d)/,
                message: 'Password must contain at least one number',
              },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-600 text-foreground mb-1.5">
          Confirm Password <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirm ? 'text' : 'password'}
            placeholder="Repeat your password"
            className="w-full border border-input rounded-xl px-4 py-2.5 pr-11 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) =>
                val === password || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
          >
            <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="text-xs text-error mt-1.5 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      {/* Terms */}
      <p className="text-xs text-muted-foreground">
        By creating an account, you agree to Bozaat&apos;s{' '}
        <a href="#" className="text-primary hover:underline">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="#" className="text-primary hover:underline">
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}